import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { useApiList } from "../api/hooks";
import type { DispatchOffer, OnlineDriver } from "../api/types";
import { DriverMap } from "../components/DriverMap";
import { Badge, DataTable, fmtDate, Page, Pagination, Toolbar } from "../components/ui";

const TONE: Record<string, "ok" | "warn" | "bad" | "neutral" | "info"> = {
  open: "warn",
  accepted: "ok",
  rejected: "bad",
  expired: "neutral",
  cancelled: "neutral",
};

/** Live view of dispatch (02 §6): who is online, and every offer with its outcome. Refreshes every 10 s. */
export function DispatchPage() {
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [tick, setTick] = useState(0);
  const query = useMemo(() => ({ status: status || undefined, page, per_page: 30, _t: tick }), [status, page, tick]);
  const offers = useApiList<DispatchOffer>("/admin/dispatch/offers", query);
  const drivers = useApiList<OnlineDriver>("/admin/dispatch/drivers", { _t: tick });

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 10_000);
    return () => clearInterval(id);
  }, []);

  const ageOf = (iso?: string | null) => {
    if (!iso) return "never";
    const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.round(s / 60)} min ago` : `${Math.round(s / 3600)} h ago`;
  };

  return (
    <Page
      title="Dispatch"
      actions={
        <span className="muted">
          Auto-refresh every 10 s ·{" "}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTick((t) => t + 1)}>
            Refresh now
          </button>
        </span>
      }
    >
      <div className="grid">
        <section className="card">
          <h2>
            Drivers online <Badge tone="info">{drivers.data.length}</Badge>
          </h2>
          <DriverMap drivers={drivers.data} />
          <p className="muted" style={{ margin: "6px 0 10px" }}>
            Green: free · blue: delivering · amber: offer pending. Tiles: OpenStreetMap until <code className="mono">map_tiles_url</code> is set in Settings.
          </p>
          <DataTable
            columns={[
              { key: "name", label: "Driver", render: (d) => (<>{d.name}{d.vendor_name && <span className="muted"> · {d.vendor_name}</span>}</>) },
              { key: "last_seen_at", label: "Position", render: (d) => (d.lat == null ? <Badge tone="bad">no position</Badge> : <span className="mono">{d.lat.toFixed(4)}, {d.lng?.toFixed(4)} · {ageOf(d.last_seen_at)}</span>) },
              { key: "active_orders", label: "Active", align: "right" },
              { key: "open_offer", label: "Offer", render: (d) => (d.open_offer ? <Badge tone="warn">pending</Badge> : "—") },
            ]}
            rows={drivers.data}
            loading={drivers.loading && drivers.data.length === 0}
            error={drivers.error}
            empty="No driver is online"
          />
        </section>
      </div>
      <h2 style={{ marginTop: 24 }}>Offers</h2>
      <Toolbar>
        <select className="input" value={status} onChange={(e) => (setStatus(e.target.value), setPage(1))}>
          <option value="">Status: all</option>
          {(["open", "accepted", "rejected", "expired", "cancelled"] as const).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </Toolbar>
      <DataTable
        columns={[
          { key: "created_at", label: "Offered", render: (o) => fmtDate(o.created_at) },
          { key: "order_code", label: "Order", render: (o) => (<Link to={`/orders/${o.order_id}`} className="mono">#{o.order_code}</Link>) },
          { key: "vendor_name", label: "Vendor" },
          { key: "driver_name", label: "Driver" },
          { key: "distance_km", label: "To pickup", align: "right", render: (o) => (o.distance_km ? `${Number(o.distance_km).toFixed(1)} km` : "—") },
          { key: "status", label: "Status", render: (o) => <Badge tone={TONE[o.status] ?? "neutral"}>{o.status}</Badge> },
          { key: "expires_at", label: "Expires", render: (o) => (o.status === "open" ? ageOf(o.expires_at).replace("ago", "left") : fmtDate(o.expires_at)) },
          { key: "responded_at", label: "Responded", render: (o) => fmtDate(o.responded_at) },
        ]}
        rows={offers.data}
        loading={offers.loading && offers.data.length === 0}
        error={offers.error}
        empty="No offers yet"
      />
      <Pagination page={page} perPage={30} total={offers.total} onPage={setPage} />
    </Page>
  );
}
